const ATLASSIAN_CANONICAL_BASE = "https://www.atlassian.com";
export const SOURCETREEAPP_CANONICAL_BASE = "https://www.sourcetreeapp.com";

const SOURCETREEAPP_MARKER = "sourcetreeapp";

function hostnameContainsSourcetreeApp(hostname: string): boolean {
    return hostname.toLowerCase().includes(SOURCETREEAPP_MARKER);
}

/**
 * Parsed route path (no leading slash), e.g. `sourcetreeapp` for `/sourcetreeapp`
 * or `ja/sourcetreeapp` for localized URLs.
 */
function pathContainsSourcetreeApp(pathWithoutLeadingSlash: string): boolean {
    return pathWithoutLeadingSlash.toLowerCase().includes(SOURCETREEAPP_MARKER);
}

function stripSourcetreeAppPathSegments(segmentPath: string): string {
    return segmentPath
        .split("/")
        .filter(
            (segment) =>
                segment.length > 0 &&
                segment.toLowerCase() !== SOURCETREEAPP_MARKER
        )
        .join("/");
}

/**
 * Path segment for `rel="canonical"` / alternates when using the SourceTree origin.
 * Removes each `sourcetreeapp` path segment so marketing URLs like
 * `/ja/sourcetreeapp/download` do not become `.../ja/sourcetreeapp/download` on
 * www.sourcetreeapp.com (target: `.../ja/download`).
 *
 * Returns `""` when the path was only `sourcetreeapp` segment(s) (use origin-only href).
 */
export function canonicalPathForHref(
    canonicalBaseUrl: string,
    segmentPath: string | undefined
): string | undefined {
    if (segmentPath === undefined || segmentPath === "") {
        return undefined;
    }
    if (canonicalBaseUrl !== SOURCETREEAPP_CANONICAL_BASE) {
        return segmentPath;
    }
    return stripSourcetreeAppPathSegments(segmentPath);
}

/**
 * Full canonical or alternate URL for link tags.
 */
export function buildCanonicalLinkHref(
    canonicalBaseUrl: string,
    segmentPath: string | undefined
): string | undefined {
    if (segmentPath === undefined) {
        return undefined;
    }
    const rest = canonicalPathForHref(canonicalBaseUrl, segmentPath);
    if (rest === undefined) {
        return undefined;
    }
    return rest === "" ? canonicalBaseUrl : `${canonicalBaseUrl}/${rest}`;
}

/**
 * Origin for `<link rel="canonical">` and hreflang alternates.
 *
 * Uses `www.sourcetreeapp.com` when:
 * - The host name includes `sourcetreeapp` (e.g. `www.sourcetreeapp.com`), or
 * - The route path includes `sourcetreeapp` (e.g. `/sourcetreeapp` on www.atlassian.com).
 */
export function getCanonicalBaseUrl(
    hostname?: string,
    pathWithoutLeadingSlash?: string
): string {
    if (hostname && hostnameContainsSourcetreeApp(hostname)) {
        return SOURCETREEAPP_CANONICAL_BASE;
    }
    if (
        pathWithoutLeadingSlash &&
        pathContainsSourcetreeApp(pathWithoutLeadingSlash)
    ) {
        return SOURCETREEAPP_CANONICAL_BASE;
    }
    return ATLASSIAN_CANONICAL_BASE;
}
